/**
 * The spotlight — dim everything, cut a hole round one control, put a card
 * beside it (TUTORIAL-STATE-001).
 *
 * ── WHY FOUR PANELS AND NOT ONE OVERLAY ────────────────────────────────────
 *
 * The usual trick for a spotlight is one element at the anchor's rectangle with
 * `box-shadow: 0 0 0 9999px rgba(...)`, which dims the rest of the screen in a
 * single line of CSS. It was rejected here: a box-shadow does not receive
 * pointer events, so every button in the dimmed area stays clickable. A user
 * two steps into a tour would click the thing the card just described, leave
 * for a page the tour was not on, and be left with a card pointing at nothing.
 *
 * Four panels — above, below, left, right of the hole — dim the screen AND
 * absorb the clicks, with no extra machinery. A fifth, transparent, covers the
 * hole itself.
 *
 * THE FIFTH ONE IS NEW, and it reverses an earlier decision. The hole was left
 * genuinely open so the spotlighted control stayed clickable — "somebody who
 * wants to try the thing the card just described should be able to". That
 * stopped being coherent when focus was trapped in the card: a mouse could
 * reach the one control a keyboard could not, while `aria-modal="true"` told
 * assistive technology nothing outside the card existed. Three answers to the
 * same question depending on how you were driving it. The hole is a window
 * now, not a door.
 *
 * ── PLACEMENT ──────────────────────────────────────────────────────────────
 *
 * Below the anchor if it fits, otherwise above, otherwise beside, and clamped
 * to the viewport either way. The sidebar is the case that drives this: it is
 * 228px wide and full height, so "below" and "above" are both wrong for it and
 * the card has to sit to its right.
 *
 * ── WHAT IT DOES NOT DO ────────────────────────────────────────────────────
 *
 * No animation on the hole between steps. A transition between two rectangles
 * looks good in a demo and, on this platform, means the dim panels are
 * mid-flight while the next step's card is already asking to be read. The hole
 * moves instantly and the card moves with it.
 */

import { useCallback, useEffect, useMemo, useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnchorRect } from './useTutorialAnchor';
import type { TutorialController } from './useTutorial';
import { useFocusTrap } from './useFocusTrap';

/** Breathing room between the hole and the highlighted element. */
const HOLE_PAD = 6;
/** Gap between the hole and the card. */
const CARD_GAP = 14;
const CARD_WIDTH = 340;
/** Enough for the longest step body at the narrowest supported width. */
const CARD_MIN_HEIGHT = 190;

interface Placement {
  top: number;
  left: number;
}

/**
 * Where the card goes for a given hole.
 *
 * Exported and pure so the test can assert the sidebar case without a DOM: a
 * full-height anchor must not push the card off the bottom of the screen,
 * which is what a naive "below, else above" does.
 */
export function placeCard(
  hole: AnchorRect,
  viewport: { width: number; height: number },
): Placement {
  const below = hole.top + hole.height + CARD_GAP;
  const above = hole.top - CARD_GAP - CARD_MIN_HEIGHT;

  let top: number;
  if (below + CARD_MIN_HEIGHT <= viewport.height) {
    top = below;
  } else if (above >= 0) {
    top = above;
  } else {
    /*
     * Neither — a tall anchor such as the sidebar. Sit level with the middle of
     * it rather than at the top of the screen, so the eye does not have to
     * travel the height of the page between the card and what it describes.
     */
    top = Math.max(8, Math.min(hole.top + hole.height / 2 - CARD_MIN_HEIGHT / 2, viewport.height - CARD_MIN_HEIGHT - 8));
  }

  /*
   * Horizontally: beside the anchor when the anchor is narrow enough to have a
   * side (the sidebar, a nav item), otherwise aligned to its left edge.
   */
  const beside = hole.left + hole.width + CARD_GAP;
  let left: number;
  if (beside + CARD_WIDTH <= viewport.width && hole.width < viewport.width / 2) {
    left = beside;
  } else {
    left = hole.left;
  }
  left = Math.max(8, Math.min(left, viewport.width - CARD_WIDTH - 8));

  return { top: Math.round(top), left: Math.round(left) };
}

function viewportSize(): { width: number; height: number } {
  return { width: window.innerWidth, height: window.innerHeight };
}

export interface TutorialSpotlightProps {
  controller: TutorialController;
}

export function TutorialSpotlight({ controller }: TutorialSpotlightProps): JSX.Element | null {
  const { t } = useTranslation('tutorial');
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { step, anchor, next, back, skip, isFirst, isLast, position, total, running } =
    controller;

  /*
   * Keyboard, on the document rather than the card: the card holds focus, but a
   * click on the dim panel moves focus to the body and the arrow keys would
   * stop working with no visible reason why.
   */
  const onKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (!running) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        skip();
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      }
    },
    [back, next, running, skip],
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  /*
   * FOCUS IS HELD IN THE CARD while the tour runs, so `aria-modal` below is a
   * fact rather than a claim. See useFocusTrap for what that covers and the one
   * thing it cannot (aria-hidden on the app root — the tour lives inside it).
   *
   * When the tour ends, focus goes to the ACCOUNT MENU button — not to the
   * launcher, which is where this pointed until 2026-09-25 and which no longer
   * works: the launcher moved inside that menu, so it exists only while the
   * menu is open, and the menu is shut by the time the tour ends. The query
   * returned null and the trap fell back to whatever had focus before — no
   * crash, and no landing place either.
   *
   * The menu button is always on screen, it is what the tour's own Account step
   * points at, and it is where "Show me around" now lives — so a screen reader
   * closing the tour lands on the door back in rather than at the top of the
   * document.
   */
  const restoreTo = useCallback(
    () => document.querySelector<HTMLElement>('[data-tutorial="account-menu"]'),
    [],
  );
  useFocusTrap(cardRef, running, restoreTo);

  /*
   * And re-focus on each step, which the trap does not do — it arms once, when
   * the tour starts. Without this the caret stays on the Next button of the
   * previous step's card while the card's content changes underneath it, so a
   * screen reader announces the new body (aria-live) while the focus ring is
   * still on a button that has been re-rendered. The card itself rather than a
   * control, because the card carries the dialog label.
   *
   * IT DEPENDS ON THE ANCHOR STATE AS WELL AS THE STEP, and that is not
   * belt-and-braces. While a step's anchor is being looked for, the spotlight
   * renders a backdrop with NO CARD — so on a step whose anchor takes a moment,
   * this effect runs against a `cardRef` that is null, focus falls to the body,
   * and nothing puts it back: the card reappearing is not a focus event, so the
   * trap's `focusin` guard never fires either. Measured in the browser on step
   * 2, where `document.activeElement` was BODY with the tour still running.
   * Re-running when the anchor resolves catches the card on its way back.
   */
  useEffect(() => {
    if (!step) return;
    cardRef.current?.focus();
  }, [step, anchor.state]);

  const hole = useMemo<AnchorRect | null>(() => {
    if (anchor.state !== 'found') return null;
    return {
      top: anchor.rect.top - HOLE_PAD,
      left: anchor.rect.left - HOLE_PAD,
      width: anchor.rect.width + HOLE_PAD * 2,
      height: anchor.rect.height + HOLE_PAD * 2,
    };
  }, [anchor]);

  if (!running || !step) return null;

  /*
   * PENDING KEEPS THE SCREEN DIMMED, WITH NO CARD.
   *
   * It drew nothing at all at first, which is wrong in both directions. A card
   * rendered centred and then jumping to the side a moment later is the fault
   * that was being avoided — but returning null means the whole overlay
   * disappears while the next anchor is being looked for, so the tour blinks
   * out and back on between steps, and on a step whose anchor is missing it is
   * gone for the length of the timeout. On a slow route that reads as the tour
   * having crashed.
   *
   * The backdrop with no card holds the tour visibly open and commits to
   * nothing. It also caught a fault in this component's own test, which had
   * been treating "no card" as "the tour ended".
   */
  if (anchor.state === 'pending') {
    return <div className="tut-plain" data-testid="tutorial-overlay-waiting" />;
  }

  const card = (
    <div
      ref={cardRef}
      className="tut-card"
      role="dialog"
      aria-modal="true"
      aria-label={t('a11y.dialog_label')}
      tabIndex={-1}
      data-testid="tutorial-card"
      style={
        hole
          ? { position: 'fixed', ...placeCard(hole, viewportSize()), width: CARD_WIDTH }
          : undefined
      }
    >
      {/*
        * Progress as a row of bars, with the words kept for screen readers.
        *
        * A bar row is read without being counted, which is what somebody
        * halfway through a tour actually wants to know. But it says nothing to
        * a screen reader, so "Step 3 of 10" stays in the DOM behind `.tut-sr`
        * — and it stays as the test hook too, because an assertion on which of
        * ten <i> elements carries a class is an assertion about the CSS.
        */}
      <div className="tut-prog" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          <i key={i} className={i < position ? 'on' : undefined} />
        ))}
      </div>
      <div className="tut-sr" data-testid="tutorial-progress">
        {t('controls.progress', { current: position, total })}
      </div>
      <div className="tut-title">{t(`steps.${step.i18nKey}.title`)}</div>
      {/*
        * aria-live so the body is announced on each step. The card is focused
        * too, but focus alone announces the label, not the changed content.
        */}
      <p className="tut-body" aria-live="polite">
        {t(`steps.${step.i18nKey}.body`)}
      </p>
      <div className="tut-actions">
        {/* Quiet, on the left, away from the thumb. Findable without being the
            easiest thing to hit by accident. */}
        <button
          type="button"
          className="tut-btn quiet"
          onClick={skip}
          data-testid="tutorial-skip"
        >
          {t('controls.skip')}
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!isFirst && (
            <button
              type="button"
              className="tut-btn"
              onClick={back}
              data-testid="tutorial-back"
            >
              {t('controls.back')}
            </button>
          )}
          <button
            type="button"
            className="tut-btn pri"
            onClick={next}
            data-testid="tutorial-next"
          >
            {isLast ? t('controls.finish') : t('controls.next')}
          </button>
        </div>
      </div>
    </div>
  );

  /*
   * No anchor: one plain backdrop and the card in the middle. This is the
   * modal fallback, and it is also how the opening and closing steps are meant
   * to look — the same presentation serving two purposes, which is why the
   * `centred` and `absent` outcomes are separate states upstream.
   */
  if (!hole) {
    return (
      <div className="tut-plain" data-testid="tutorial-overlay-plain">
        {card}
      </div>
    );
  }

  const vp = viewportSize();
  return (
    <div data-testid="tutorial-overlay">
      {/*
        * The four dim panels. Each one absorbs clicks; between them they leave
        * exactly the hole open.
        */}
      <div className="tut-dim" style={{ top: 0, left: 0, width: '100%', height: Math.max(0, hole.top) }} />
      <div
        className="tut-dim"
        style={{
          top: hole.top + hole.height,
          left: 0,
          width: '100%',
          height: Math.max(0, vp.height - (hole.top + hole.height)),
        }}
      />
      <div
        className="tut-dim"
        style={{ top: Math.max(0, hole.top), left: 0, width: Math.max(0, hole.left), height: hole.height }}
      />
      <div
        className="tut-dim"
        style={{
          top: Math.max(0, hole.top),
          left: hole.left + hole.width,
          width: Math.max(0, vp.width - (hole.left + hole.width)),
          height: hole.height,
        }}
      />
      {/*
        * THE FIFTH PANEL — invisible, over the hole, and the other half of the
        * focus trap.
        *
        * The hole used to be genuinely open, so the spotlighted control stayed
        * clickable. That was a deliberate choice and it stopped being coherent
        * the moment focus was trapped in the card: a mouse could reach the one
        * control a keyboard could not, while `aria-modal="true"` told assistive
        * technology that nothing outside the card was available at all. Three
        * different answers to the same question depending on how you were
        * driving.
        *
        * So the hole is now a window rather than a door. It shows you where the
        * thing is; Next, Back and Skip are how you move. Transparent, not
        * dimmed — the point of the hole is that the control stays lit.
        */}
      <div
        className="tut-block"
        data-testid="tutorial-block"
        style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
      />
      {/* The ring. Pointer-events off, so it does not re-cover the hole. */}
      <div
        className="tut-ring"
        data-testid="tutorial-ring"
        style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
      />
      {card}
    </div>
  );
}
